import axios from 'axios';
import {
  sanitizeInput,
  validateFileSecurely,
  validateAuth,
  checkRateLimit,
  handleApiError,
  FILE_RESTRICTIONS
} from './validationUtils.js';
import { uploadLargeFileWithSignedUrl, needsSignedUrlUpload } from './signedUrlUploadService.js';
import { compressFile } from './mediaCompressionService.js';

// Utility function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MEDIA_API_URL = process.env.REACT_APP_API_MEDIA_URL || API_BASE_URL;
const USER_API_URL = process.env.REACT_APP_API_USER_URL || API_BASE_URL;

// Simple cache to prevent duplicate uploads
const uploadCache = new Map();

const generateUUID = () => {
  // Check if crypto.randomUUID is available
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  
  // Fallback implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Enhanced media type detection with comprehensive checks
 * @param {File} file - The file to analyze
 * @param {string} url - Optional URL for additional detection
 * @returns {string} The detected media type
 */
const detectMediaType = (file, url = null) => {
  // PRIORITY 1: Check file MIME type if file is provided
  if (file && file.type) {
    console.log('🔍 Detecting media type from file:', {
      name: file.name,
      type: file.type,
      size: file.size
    });
    
    const mimeType = file.type.toLowerCase();
    
    // Video detection
    if (mimeType.startsWith('video/')) {
      console.log('✅ Detected as VIDEO from MIME type:', mimeType);
      return 'video';
    }
    
    // Image detection
    if (mimeType.startsWith('image/')) {
      console.log('✅ Detected as IMAGE from MIME type:', mimeType);
      return 'image';
    }
    
    // Audio detection
    if (mimeType.startsWith('audio/')) {
      console.log('✅ Detected as AUDIO from MIME type:', mimeType);
      return 'audio';
    }
    
    // Document detection
    if (mimeType.includes('pdf') || 
        mimeType.includes('document') || 
        mimeType.includes('text/') ||
        mimeType.includes('application/msword') ||
        mimeType.includes('application/vnd.openxmlformats-officedocument')) {
      console.log('✅ Detected as DOCUMENT from MIME type:', mimeType);
      return 'document';
    }
  }
  
  // PRIORITY 2: Check file extension if MIME type is not conclusive
  let fileName = '';
  if (file && file.name) {
    fileName = file.name.toLowerCase();
  } else if (url) {
    fileName = url.toLowerCase();
  }
  
  if (fileName) {
    console.log('🔍 Checking file extension for:', fileName);
    
    // Video extensions
    const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v', '.wmv', '.flv', '.3gp', '.ogg', '.ogv'];
    if (videoExtensions.some(ext => fileName.includes(ext))) {
      console.log('✅ Detected as VIDEO from file extension');
      return 'video';
    }
    
    // Image extensions
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tiff'];
    if (imageExtensions.some(ext => fileName.includes(ext))) {
      console.log('✅ Detected as IMAGE from file extension');
      return 'image';
    }
    
    // Audio extensions
    const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac'];
    if (audioExtensions.some(ext => fileName.includes(ext))) {
      console.log('✅ Detected as AUDIO from file extension');
      return 'audio';
    }
    
    // Document extensions
    const documentExtensions = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.xls', '.xlsx', '.ppt', '.pptx'];
    if (documentExtensions.some(ext => fileName.includes(ext))) {
      console.log('✅ Detected as DOCUMENT from file extension');
      return 'document';
    }
  }
  
  // PRIORITY 3: Check URL patterns if available
  if (url) {
    const urlLower = url.toLowerCase();
    console.log('🔍 Checking URL patterns for:', urlLower);
    
    if (urlLower.includes('/video/') || 
        urlLower.includes('video') || 
        urlLower.includes('blob.core.windows.net/video')) {
      console.log('✅ Detected as VIDEO from URL pattern');
      return 'video';
    }
    
    if (urlLower.includes('/image/') || 
        urlLower.includes('image')) {
      console.log('✅ Detected as IMAGE from URL pattern');
      return 'image';
    }
  }
  
  // Default fallback
  console.log('⚠️ Could not determine media type, defaulting to "file"');
  return 'file';
};

/**
 * Upload just the media file to get a URL
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Optional progress callback for large files
 * @returns {Promise<Object>} A promise that resolves to the uploaded media data including URL
 */
export const uploadMediaFile = async (file, onProgress = null) => {
  const token = validateAuth();
  
  try {
    checkRateLimit('uploadMediaFile', 5, 60000); // 5 uploads per minute

    // Security validation before upload
    const mediaType = validateFileSecurely(file);

    // Check cache first
    const cacheKey = `${file.name}-${file.size}-${file.lastModified}`;
    if (uploadCache.has(cacheKey)) {
      return uploadCache.get(cacheKey);
    }

    // Sanitize filename
    const sanitizedFile = new File([file], sanitizeInput.filename(file.name), {
      type: file.type,
      lastModified: file.lastModified
    });

    // Compress the file before upload
    console.log('🗜️ Starting compression:', {
      originalSize: formatFileSize(sanitizedFile.size),
      type: sanitizedFile.type
    });

    const fileToUpload = await compressFile(sanitizedFile, (progress) => {
      // Compression progress (mainly for videos)
      if (onProgress) {
        onProgress({
          percentage: Math.round(progress * 50), // Reserve 0-50% for compression, 50-100% for upload
          phase: 'compressing',
          uploadedBytes: 0,
          totalBytes: sanitizedFile.size
        });
      }
    });

    if (fileToUpload !== sanitizedFile) {
      console.log('✅ Compression complete:', {
        originalSize: formatFileSize(sanitizedFile.size),
        compressedSize: formatFileSize(fileToUpload.size),
        reduction: `${Math.round((1 - fileToUpload.size / sanitizedFile.size) * 100)}%`
      });
    }

    let result = {};

    // Check if file needs signed URL upload (for files >100MB)
    if (needsSignedUrlUpload(fileToUpload)) {

      try {
        const uploadUrl = await uploadLargeFileWithSignedUrl(fileToUpload, (progress) => {
          if (onProgress) {
            // Adjust progress to 50-100% range (0-50% was compression)
            onProgress({
              ...progress,
              percentage: 50 + Math.round(progress.percentage * 0.5)
            });
          }
        });

        result = {
          url: sanitizeInput.url(uploadUrl),
          id: generateUUID(),
          uploadMethod: 'signed-url'
        };
      } catch (signedUrlError) {
        console.error('❌ Signed URL upload failed:', {
          error: signedUrlError.message,
          stack: signedUrlError.stack,
          fileSize: fileToUpload.size,
          fileName: fileToUpload.name
        });
        throw signedUrlError;
      }
    } else {
      // Use existing backend upload for files ≤100MB

      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await axios.post(
        `${MEDIA_API_URL}/upload`,
        formData,
        {
          headers: {
            'Authorization': `${token}`
          },
          onUploadProgress: progressEvent => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (onProgress) {
              // Adjust progress to 50-100% range (0-50% was compression)
              onProgress({
                percentage: 50 + Math.round(percentCompleted * 0.5),
                uploadedBytes: progressEvent.loaded,
                totalBytes: progressEvent.total
              });
            }
          }
        }
      );
      
      // Extract URL and other data from response
      if (typeof response.data === 'string') {
        result = { url: sanitizeInput.url(response.data) };
      } else if (response.data.result && typeof response.data.result === 'string') {
        result = { url: sanitizeInput.url(response.data.result) };
      } else if (response.data.result && response.data.result.url) {
        result = { ...response.data.result, url: sanitizeInput.url(response.data.result.url) };
      } else if (response.data.url) {
        result = { ...response.data, url: sanitizeInput.url(response.data.url) };
      } else if (response.data.result) {
        result = { ...response.data.result };
        if (result.url) {
          result.url = sanitizeInput.url(result.url);
        }
      } else {
        // Try to extract URL from other possible formats
        let mediaUrl = response.data.path || 
                      response.data.location || 
                      (response.data.result && response.data.result.path) ||
                      (response.data.result && response.data.result.location) ||
                      (response.data.data && response.data.data.url) ||
                      (response.data.data && response.data.data.path);
        
        if (mediaUrl) {
          result = { url: sanitizeInput.url(mediaUrl) };
        }
      }

      result.uploadMethod = 'backend-enhanced';
    }
    
    if (!result.url) {
      console.error("Could not extract URL from upload response.");
      throw new Error("URL not found in upload response");
    }
    
    // Ensure we have an id for the media
    if (!result.id) {
      result.id = generateUUID();
    }
    
    console.log('✅ File uploaded successfully:', {
      url: result.url,
      id: result.id,
      method: result.uploadMethod
    });
    
    // Cache the result
    uploadCache.set(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error("Error uploading media:", error);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
    }
    throw handleApiError(error, "uploadMediaFile");
  }
};

/**
 * Upload media file to the server and associate it with a profile field
 * @param {File} file - The file to upload
 * @param {String} fieldId - The profile field ID
 * @param {String} valueId - The value ID (item ID) to associate the media with
 * @returns {Promise} A promise that resolves to the uploaded media data
 */
export const uploadMedia = async (file, fieldId, valueId) => {
  const token = validateAuth();
  
  try {
    checkRateLimit('uploadMedia', 5, 60000); // 5 uploads per minute

    // Enhanced validation with better error messages
    if (!file) {
      throw new Error("No file provided for upload");
    }
    
    if (!fieldId) {
      throw new Error("No field ID provided for media upload");
    }
    
    if (!valueId) {
      throw new Error("No value ID provided for media upload");
    }

    console.log('🚀 Starting uploadMedia process:', {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      fieldId,
      valueId
    });

    // Validate and sanitize IDs
    const sanitizedFieldId = sanitizeInput.uuid(fieldId);
    const sanitizedValueId = sanitizeInput.uuid(valueId);
    
    // Step 1: Upload the file to get a URL
    const mediaData = await uploadMediaFile(file);
    
    if (!mediaData || !mediaData.url) {
      throw new Error("No media URL returned from upload");
    }
    
    // Step 2: Enhanced media type detection
    const detectedMediaType = detectMediaType(file, mediaData.url);
    
    console.log('📋 Media type detection result:', {
      fileName: file.name,
      mimeType: file.type,
      detectedType: detectedMediaType,
      url: mediaData.url
    });
    
    // Validate media type
    const allowedMediaTypes = ['image', 'video', 'audio', 'document', 'file'];
    if (!allowedMediaTypes.includes(detectedMediaType)) {
      console.error('❌ Invalid media type detected:', detectedMediaType);
      throw new Error(`Invalid media type: ${detectedMediaType}`);
    }
    
    // Step 3: Associate the URL with the profile field
    
    // Generate a unique mediaId if not provided
    const mediaId = generateUUID();

    const payload = {
      id: sanitizedFieldId, 
      valueId: sanitizedValueId,  
      type: sanitizeInput.text(detectedMediaType), // Use detected type
      url: mediaData.url,
      mediaId: sanitizeInput.uuid(mediaId)
    };
    
    console.log('📝 Sending payload to profile field API:', {
      ...payload,
      detectedMediaType // Include for debugging
    });
    
    const response = await axios.put(
      `${USER_API_URL}/media/profile-field`, 
      payload,
      {
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    console.log('✅ Profile field update successful:', response.data);

    const finalResult = {
      id: mediaId,
      type: detectedMediaType, // Use detected type
      url: mediaData.url
    };

    console.log('🎉 uploadMedia completed successfully:', finalResult);

    return finalResult;
  } catch (error) {
    console.error('❌ Error in uploadMedia:', error);
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
    }
    throw handleApiError(error, "uploadMedia");
  }
};

/**
 * Delete a media item
 * @param {String} mediaId - The ID of the media to delete
 * @param {String} fieldId - The ID of the profile field (optional, will be retrieved from context if available)
 * @param {String} valueId - The ID of the value item (optional, will be retrieved from context if available)
 * @returns {Promise} A promise that resolves when the media is deleted
 */
export const deleteMedia = async (mediaId, fieldId = null, valueId = null) => {
  const token = validateAuth();
  
  try {
    checkRateLimit('deleteMedia', 10, 60000); // 10 deletions per minute

    // Validate media ID
    const sanitizedMediaId = sanitizeInput.uuid(mediaId);
    
    // Create proper payload matching the API expectations
    const payload = { mediaId: sanitizedMediaId };
    
    // Add fieldId and valueId to payload if they were provided
    if (fieldId) {
      payload.id = sanitizeInput.uuid(fieldId);
    }
    if (valueId) {
      payload.valueId = sanitizeInput.uuid(valueId);
    }
    
    // Use POST method based on the server implementation that returns status 201
    const response = await axios.put(
      `${USER_API_URL}/profile-media/remove`, 
      payload,
      {
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    
    return response.data;
  } catch (error) {
    console.error('Error deleting media:', error);
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
    }
    throw handleApiError(error, "deleteMedia");
  }
};

// Export other functions from profileFieldsMediaService if needed
export const findProfileFieldByTitle = (profileFields, title) => {
  if (!Array.isArray(profileFields)) return null;
  
  // Sanitize the title for safe comparison
  const sanitizedTitle = sanitizeInput.text(title);
  if (!sanitizedTitle) return null;
  
  return profileFields.find(field => 
    field.title && sanitizeInput.text(field.title) === sanitizedTitle
  ) || null;
};

export const PROFILE_FIELD_TYPES = {
  TRIPS_EXPERIENCES: 'Trips, Events & Experiences',
  PRESENTATIONS_AWARDS: 'Presentations & Awards',
  COMMUNITY_CHARITY: 'Community / Charity Work'
};

export const getProfileFieldValue = (profileFields, fieldType) => {
  const field = findProfileFieldByTitle(profileFields, fieldType);
  return field?.value || [];
};

// Export validation functions for use in other services
export { 
  sanitizeInput, 
  validateFileSecurely,
  checkRateLimit,
  handleApiError,
  FILE_RESTRICTIONS 
};